import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { RouteComponentProps } from "@reach/router";
import "./MetaBuyingDrafts.scss";
import MetaTableWidget from "./Components/MetaTableWidget";
import {
  Button,
  ButtonType,
  CheckBox,
  Header,
  OverlayTrigger,
  ToggleSwitch,
  FilterBarTokens,
  ButtonFrameworkVariant,
} from "../../Components";
import { MdCancel, MdCheck, MdClose, MdMoreVert, MdRemoveRedEye } from "react-icons/md";
import {
  AdAccountInfo,
  MetaBuyingTableRow,
  CampaignRow,
  AdSetRow,
  AdRow,
  MBApprovalStage,
} from "@blisspointmedia/bpm-types/dist/MetaBuying";
import * as R from "ramda";
import {
  UseAlertDialog,
  DESELECT_ROW,
  MBAlertDialogType,
  MessageIcon,
  UseReviewModal,
  SELECT_ROW,
  UseTableData,
  useAlertDialog,
  useFilterBar,
  useGetButtonPosition,
  useMetaBuyingTable,
  useReviewModal,
  useSelectRow,
  useTableData,
  useTableHeadersRenderer,
  UseChangeLogModal,
  useChangeLogModal,
  formatAsset,
} from "../MetaBuyingUtils";
import MoreActionsMenu from "./Components/MoreActionsMenu";
import { createPortal } from "react-dom";
import { Tooltip } from "react-bootstrap";
import { ReviewModalTab, ReviewModalVariant } from "./Components/ReviewModal/ReviewModal";
import { FormContext, metaBuyingFormDefault } from "../MetaBuyingContext";
import { PageTab } from "../MetaBuying";
import {
  TAB_CAMPAIGN,
  TAB_AD_SET,
  TAB_AD,
  VERTICAL,
  HORIZONTAL,
  SQUARE,
} from "../MetaBuyingConstants";

interface MetaBuyingDraftsProps {
  isInternal: boolean;
  selectedAdAccount: AdAccountInfo;
  setSelectedAdAccount: React.Dispatch<React.SetStateAction<AdAccountInfo>>;
  adAccountOptions: AdAccountInfo[];
  campaignRows: CampaignRow[];
  adSetRows: AdSetRow[];
  adRows: AdRow[];
  refreshData: (tab: PageTab) => void;
  goToTab: (newTab: any) => void;
  tabIndex: number;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  creationTabSelections: {
    [TAB_CAMPAIGN]: boolean;
    [TAB_AD_SET]: boolean;
    [TAB_AD]: boolean;
  };
  setCreationTabSelections: React.Dispatch<
    React.SetStateAction<{
      [TAB_CAMPAIGN]: boolean;
      [TAB_AD_SET]: boolean;
      [TAB_AD]: boolean;
    }>
  >;
  selectionsSubmitted: boolean;
  setSelectionsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
}

const MetaBuyingDrafts: React.FC<MetaBuyingDraftsProps & RouteComponentProps> = React.memo(
  ({
    isInternal,
    selectedAdAccount,
    setSelectedAdAccount,
    adAccountOptions,
    campaignRows,
    adSetRows,
    adRows,
    refreshData,
    goToTab,
    setTabIndex,
    setCreationTabSelections,
    setSelectionsSubmitted,
  }: MetaBuyingDraftsProps & RouteComponentProps): JSX.Element => {
    const { metaBuyingForm, setMetaBuyingForm } = useContext(FormContext);

    const metaAdForm = metaBuyingForm.ad;

    const setMetaAdForm = useCallback(
      value =>
        setMetaBuyingForm((current: any): typeof metaBuyingFormDefault => {
          return { ...current, ad: value };
        }),
      [setMetaBuyingForm]
    );

    // Used to manage table data
    const {
      setFilter,
      selectedLevel,
      setSelectedLevel,
      selectedRows,
      setSelectedRows,
      selectAll,
      setSelectAll,
      tableData,
      filteredTableData,
    }: UseTableData = useTableData({
      isInternal,
      campaignRows,
      adSetRows,
      adRows,
    });

    const selectRow: (row: MetaBuyingTableRow, remove?: boolean) => void = useSelectRow({
      setSelectedRows,
      selectedLevel,
    });

    const tableHeadersRenderer: ({
      data,
      columnIndex,
    }: any) => JSX.Element = useTableHeadersRenderer({
      filteredTableData,
      selectAll,
      setSelectAll,
      selectedRows,
      setSelectedRows,
      selectedLevel,
    });

    const buttonRefs = useRef({}); // Object to hold refs for each row
    const getButtonPosition: (
      rowId: string
    ) => {
      top: number;
      left: number;
    } = useGetButtonPosition({ buttonRefs, selectedLevel });
    const menuRef: React.LegacyRef<HTMLDivElement> | undefined = useRef(null);
    const [activeActionMenuRow, setActiveActionMenuRow] = useState<MetaBuyingTableRow>();
    // Close menu if clicking outside of it
    useEffect(() => {
      const handleOutsideClick = event => {
        if (
          menuRef &&
          menuRef.current &&
          !menuRef.current.contains(event.target) &&
          !buttonRefs.current[activeActionMenuRow?.id || ""]?.contains(event.target)
        ) {
          const filteredTableRows = Object.values(
            selectedRows[selectedLevel] as Record<string, MetaBuyingTableRow>
          ).filter(row => row.id !== activeActionMenuRow?.id);
          // Filter out current active row from selected rows
          setSelectedRows(current => {
            let newSelectedRows = {};
            for (let row of filteredTableRows) {
              newSelectedRows[row.id] = row;
            }

            return {
              ...current,
              [selectedLevel]: newSelectedRows,
            };
          });
          setActiveActionMenuRow(undefined);
        }
      };

      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }, [activeActionMenuRow, menuRef, buttonRefs, selectedRows, selectedLevel, setSelectedRows]);

    // Used to manage alert dialogs
    const {
      alertDialogType,
      alertDialogActivatedRow,
      openAlertDialog,
      alertDialogComponent,
    }: UseAlertDialog = useAlertDialog({
      isInternal,
      accountID: selectedAdAccount.account_id,
      businessManager: selectedAdAccount.business_manager,
      selectedLevel,
      selectedRows,
      selectRow,
      campaignRows,
      adSetRows,
      adRows,
      refreshData,
      goToTab,
      setTabIndex,
      setCreationTabSelections,
      setSelectionsSubmitted,
    });

    // Used to manage review modal
    const {
      setReviewModalActivatedRow,
      reviewModalSetSelectedTab,
      reviewModalComponent,
    }: UseReviewModal = useReviewModal({
      reviewModalVariant: ReviewModalVariant.DRAFT,
      selectedAdAccount,
      selectedLevel,
      isInternal,
      selectRow,
      openAlertDialog,
      goToTab,
      setTabIndex,
      setCreationTabSelections,
      metaAdForm,
      setMetaAdForm,
      setSelectionsSubmitted,
    });

    // Used to manage change log modal
    const {
      setChangeLogModalActivatedRow,
      changeLogModalComponent,
    }: UseChangeLogModal = useChangeLogModal({
      variant: "drafts",
      accountId: selectedAdAccount.account_id,
      selectRow,
    });

    const tableColumns: Header[] = useMemo(() => {
      let headers: Header[] = [
        {
          label: "",
          name: "id",
          width: 40,
          nonInteractive: true,
          renderer: (row: MetaBuyingTableRow) => {
            return (
              <CheckBox
                className="checkboxCell"
                checked={R.has(row.id, selectedRows[selectedLevel])}
                onCheck={() => selectRow(row)}
              />
            );
          },
        },
      ];

      // For client facing table
      if (!isInternal) {
        headers.push(
          ...[
            {
              label: "Ad",
              name: "ad_name",
              flex: 4,
              renderer: (row: AdRow) => {
                return <div className="adNameCell highlighted">{row.ad_name}</div>;
              },
            },
            {
              label: "Ad Set",
              name: "adset_name",
              flex: 4,
              renderer: (row: AdSetRow) => {
                return <div className="adSetNameCell">{row.adset_name}</div>;
              },
            },
            {
              label: "Campaign",
              name: "campaign_name",
              flex: 4,
              renderer: (row: CampaignRow) => {
                return <div className="campaignNameCell">{row.campaign_name}</div>;
              },
            },
            {
              label: "Created On",
              name: "created",
              width: 115,
              renderer: (row: MetaBuyingTableRow) => {
                return (
                  <div className="createdCell">
                    {row.created ? new Date(row.created).toLocaleString() : "No Date"}
                  </div>
                );
              },
            },
            {
              width: 381,
              nonInteractive: true,
              renderer: (row: AdRow) => {
                return (
                  <div className="actionsCell">
                    <Button
                      className="viewAdPreviewButton"
                      variant={ButtonFrameworkVariant.TRAILING_ICON}
                      icon={<MdRemoveRedEye />}
                      type={ButtonType.OUTLINED}
                      size="sm"
                      onClick={() => {
                        setSelectedRows(current => {
                          return { ...current, ad: { [row.id]: row } };
                        });
                        setReviewModalActivatedRow(row);
                        reviewModalSetSelectedTab(ReviewModalTab.AD_PREVIEW);
                      }}
                    >
                      View Ad Preview
                    </Button>
                    {row.approval_stage === MBApprovalStage.NONE && (
                      <div className="approvalPlaceholder">No Approval Requests</div>
                    )}
                    {row.approval_stage === MBApprovalStage.IN_REVIEW && (
                      <div className="approvalSection">
                        <div className="approvalLabel">Approve?</div>
                        <Button
                          className="approveButton"
                          variant={ButtonFrameworkVariant.ICON_ONLY}
                          icon={<MdCheck />}
                          type={ButtonType.OUTLINED}
                          size="sm"
                          onClick={() => {
                            setSelectedRows(current => {
                              return { ...current, ad: { [row.id]: row } };
                            });
                            openAlertDialog(MBAlertDialogType.APPROVED, row);
                          }}
                        />
                        <Button
                          className="rejectButton"
                          variant={ButtonFrameworkVariant.ICON_ONLY}
                          icon={<MdClose />}
                          type={ButtonType.OUTLINED}
                          size="sm"
                          onClick={() => {
                            setSelectedRows(current => {
                              return { ...current, ad: { [row.id]: row } };
                            });
                            openAlertDialog(MBAlertDialogType.CHANGES_REQUESTED, row);
                          }}
                        />
                      </div>
                    )}
                    {row.approval_stage === MBApprovalStage.CHANGES_REQUESTED && (
                      <div className="approvalSection">
                        <Button
                          className="unapprovedButton"
                          variant={ButtonFrameworkVariant.TRAILING_ICON}
                          type={ButtonType.OUTLINED}
                          size="sm"
                          icon={<MdCancel />}
                          onClick={() => {
                            setSelectedRows(current => {
                              return { ...current, ad: { [row.id]: row } };
                            });
                            openAlertDialog(MBAlertDialogType.STATUS_REMOVED, row);
                          }}
                        >
                          Unapproved
                        </Button>
                      </div>
                    )}
                    {row.approval_stage === MBApprovalStage.APPROVED && (
                      <div className="approvalSection">
                        <Button
                          className="approvedButton"
                          variant={ButtonFrameworkVariant.TRAILING_ICON}
                          type={ButtonType.OUTLINED}
                          size="sm"
                          icon={<MdCancel />}
                          onClick={() => {
                            setSelectedRows(current => {
                              return { ...current, ad: { [row.id]: row } };
                            });
                            openAlertDialog(MBAlertDialogType.STATUS_REMOVED, row);
                          }}
                        >
                          Approved
                        </Button>
                      </div>
                    )}
                  </div>
                );
              },
            },
          ]
        );
      } else {
        if (selectedLevel === "ad") {
          headers.push(
            ...[
              {
                label: "Ad",
                name: "ad_name",
                flex: 4,
                renderer: (row: AdRow) => {
                  return <div className="adNameCell highlighted">{row.ad_name}</div>;
                },
              },
              {
                label: "Ad Set",
                name: "adset_name",
                flex: 4,
                renderer: (row: AdSetRow) => {
                  return <div className="adSetNameCell">{row.adset_name}</div>;
                },
              },
              {
                label: "Campaign",
                name: "campaign_name",
                flex: 4,
                renderer: (row: CampaignRow) => {
                  return <div className="campaignNameCell">{row.campaign_name}</div>;
                },
              },
            ]
          );
        } else if (selectedLevel === "adset") {
          headers.push(
            ...[
              {
                label: "Ad Set",
                name: "adset_name",
                flex: 4,
                renderer: (row: AdSetRow) => {
                  return <div className="adSetNameCell highlighted">{row.adset_name}</div>;
                },
              },
              {
                label: "Campaign",
                name: "campaign_name",
                flex: 4,
                renderer: (row: CampaignRow) => {
                  return <div className="campaignNameCell">{row.campaign_name}</div>;
                },
              },
            ]
          );
        } else {
          headers.push({
            label: "Campaign",
            name: "campaign_name",
            flex: 4,
            renderer: (row: CampaignRow) => {
              return <div className="campaignNameCell highlighted">{row.campaign_name}</div>;
            },
          });
        }

        headers.push(
          ...[
            {
              label: "Created On",
              name: "created",
              width: 115,
              renderer: (row: MetaBuyingTableRow) => {
                return (
                  <div className="createdCell">
                    {row.created ? new Date(row.created).toLocaleString() : "No Date"}
                  </div>
                );
              },
            },
            {
              label: "Creator",
              name: "lastuser",
              flex: 2,
              renderer: (row: MetaBuyingTableRow) => {
                return <div className="creatorCell">{row.lastuser}</div>;
              },
            },
          ]
        );
        if (selectedLevel === "ad") {
          headers.push(
            {
              label: "Client-Facing",
              name: "client_facing",
              width: 130,
              renderer: (row: AdRow) => {
                let toggleOn;
                if (
                  alertDialogType === MBAlertDialogType.CLIENT_FACING_DISABLED &&
                  alertDialogActivatedRow?.id === row.id
                ) {
                  // If we are turning off the row's toggle
                  toggleOn = false;
                } else if (
                  alertDialogType === MBAlertDialogType.CLIENT_FACING_ENABLED &&
                  alertDialogActivatedRow?.id === row.id
                ) {
                  // If we are turning on the row's toggle
                  toggleOn = true;
                } else if (
                  alertDialogType === MBAlertDialogType.CLIENT_FACING_DISABLED &&
                  selectedRows.ad[row.id]
                ) {
                  // If row is selected and we are turning off another row's toggle
                  toggleOn = false;
                } else if (
                  alertDialogType === MBAlertDialogType.CLIENT_FACING_ENABLED &&
                  selectedRows.ad[row.id]
                ) {
                  // If row is selected and we are turning on another row's toggle
                  toggleOn = true;
                } else if (row.client_facing) {
                  toggleOn = true;
                } else {
                  toggleOn = false;
                }

                return (
                  <div className="clientFacingCell">
                    <ToggleSwitch
                      label={""}
                      labelPosition="none"
                      checked={toggleOn}
                      onChange={() => {
                        if (row.client_facing) {
                          const filteredAdRows = Object.values(
                            selectedRows.ad as Record<string, AdRow>
                          ).filter(ad => ad.client_facing);
                          // Keep ads that are client facing selected since
                          // only those need to be updated to not be client facing
                          setSelectedRows(current => {
                            let newSelectedAds = {};
                            for (let ad of filteredAdRows) {
                              newSelectedAds[ad.id] = ad;
                            }

                            return {
                              ...current,
                              ad: { ...newSelectedAds, [row.id]: row },
                            };
                          });
                          openAlertDialog(MBAlertDialogType.CLIENT_FACING_DISABLED, row);
                        } else {
                          const filteredAdRows = Object.values(
                            selectedRows.ad as Record<string, AdRow>
                          ).filter(ad => !ad.client_facing);
                          // Keep ads that are not client facing selected since
                          // only those need to be updated to be client facing
                          setSelectedRows(current => {
                            let newSelectedAds = {};
                            for (let ad of filteredAdRows) {
                              newSelectedAds[ad.id] = ad;
                            }

                            return {
                              ...current,
                              ad: { ...newSelectedAds, [row.id]: row },
                            };
                          });
                          openAlertDialog(MBAlertDialogType.CLIENT_FACING_ENABLED, row);
                        }
                      }}
                    />
                  </div>
                );
              },
            },
            {
              label: "Approval Status",
              name: "approval_stage",
              width: 175,
              renderer: (row: AdRow) => {
                if (row.approval_stage === "NONE" || row.approval_stage === "CHANGES_REQUESTED") {
                  return (
                    <div className="requestApprovalCell">
                      {!row.notes && (
                        <Button
                          disabled={!row.client_facing}
                          type={ButtonType.FILLED}
                          design="secondary"
                          size="sm"
                          onClick={() => {
                            let filteredAdRows = Object.values(
                              selectedRows.ad as Record<string, AdRow>
                            ).filter(ad => {
                              return (
                                (ad.approval_stage === "NONE" ||
                                  ad.approval_stage === "CHANGES_REQUESTED") &&
                                ad.client_facing
                              );
                            });
                            // Filter out ads that aren't ready for requesting approval
                            setSelectedRows(current => {
                              let newSelectedAds = {};
                              for (let ad of filteredAdRows) {
                                newSelectedAds[ad.id] = ad;
                              }

                              return {
                                ...current,
                                ad: { ...newSelectedAds, [row.id]: row },
                              };
                            });
                            openAlertDialog(MBAlertDialogType.REQUEST_APPROVAL, row);
                          }}
                        >
                          Request Approval
                        </Button>
                      )}
                      {row.notes && (
                        <Button
                          className="unapprovedCell"
                          type={ButtonType.FILLED}
                          variant={ButtonFrameworkVariant.TRAILING_ICON}
                          icon={<MessageIcon color="white" />}
                          size="sm"
                          tooltipText={row.notes}
                          enableAnimation={false}
                          onClick={() => {
                            let filteredAdRows = Object.values(
                              selectedRows.ad as Record<string, AdRow>
                            ).filter(ad => {
                              return (
                                (ad.approval_stage === "NONE" ||
                                  ad.approval_stage === "CHANGES_REQUESTED") &&
                                ad.client_facing
                              );
                            });
                            // Filter out ads that aren't ready for requesting approval
                            setSelectedRows(current => {
                              let newSelectedAds = {};
                              for (let ad of filteredAdRows) {
                                newSelectedAds[ad.id] = ad;
                              }

                              return {
                                ...current,
                                ad: { ...newSelectedAds, [row.id]: row },
                              };
                            });
                            openAlertDialog(MBAlertDialogType.REQUEST_APPROVAL, row);
                          }}
                        >
                          Unapproved
                        </Button>
                      )}
                    </div>
                  );
                } else if (row.approval_stage === "IN_REVIEW") {
                  return <div className="pendingCell">Pending</div>;
                } else {
                  return row.notes ? (
                    <OverlayTrigger
                      placement="bottom center"
                      overlay={<Tooltip id={`id:${row.notes}`}>{row.notes}</Tooltip>}
                    >
                      <div className="approvedCell">
                        <div className="approvedCellLabel">Approved</div>
                        <MessageIcon color="black" />
                      </div>
                    </OverlayTrigger>
                  ) : (
                    <div className="approvedCell">
                      <div className="approvedCellLabel">Approved</div>
                    </div>
                  );
                }
              },
            }
          );
        }

        headers.push({
          width: 58,
          nonInteractive: true,
          renderer: (row: MetaBuyingTableRow) => {
            const buttonPosition = getButtonPosition(row.id);

            return (
              <div className="actionsCell">
                <div className="moreActionsContainer">
                  <Button
                    ref={el => {
                      buttonRefs.current[row.id] = el;
                    }} // Assign ref for each button
                    variant={ButtonFrameworkVariant.ICON_ONLY}
                    type={ButtonType.FILLED}
                    icon={<MdMoreVert />}
                    size="sm"
                    onClick={() => {
                      if (
                        selectedRows[selectedLevel][row.id] &&
                        activeActionMenuRow?.id === row.id
                      ) {
                        selectRow(row, DESELECT_ROW);
                      } else {
                        selectRow(row, SELECT_ROW);
                      }
                      setActiveActionMenuRow(activeActionMenuRow?.id === row.id ? undefined : row);
                    }}
                  />
                  {activeActionMenuRow?.id === row.id &&
                    createPortal(
                      <div
                        style={{
                          borderRadius: "6px",
                          backgroundColor: "white",
                          position: "fixed",
                          top: buttonPosition.top,
                          left: buttonPosition.left,
                          display: "flex",
                          zIndex: 1000,
                        }}
                        ref={menuRef}
                      >
                        <MoreActionsMenu
                          selectedRows={selectedRows}
                          selectedLevel={selectedLevel}
                          tab="drafts"
                          viewDetailsAction={() => {
                            setSelectedRows(current => {
                              return { ...current, [selectedLevel]: { [row.id]: row } };
                            });
                            reviewModalSetSelectedTab(ReviewModalTab.DETAILS);
                            setActiveActionMenuRow(undefined);
                            setReviewModalActivatedRow(row);
                          }}
                          viewAdPreviewAction={() => {
                            setSelectedRows(current => {
                              return { ...current, [selectedLevel]: { [row.id]: row } };
                            });
                            reviewModalSetSelectedTab(ReviewModalTab.AD_PREVIEW);
                            setActiveActionMenuRow(undefined);
                            setReviewModalActivatedRow(row);
                          }}
                          deleteAction={() => {
                            setSelectedRows(current => {
                              return { ...current, [selectedLevel]: { [row.id]: row } };
                            });
                            setActiveActionMenuRow(undefined);
                            openAlertDialog(MBAlertDialogType.DELETE, row);
                          }}
                          editAction={() => {
                            setSelectedRows(current => {
                              return { ...current, [selectedLevel]: { [row.id]: row } };
                            });
                            setActiveActionMenuRow(undefined);
                            // TODO: Revisit setting the form data to fix instagram account id
                            if (selectedLevel === "ad") {
                              const adRow = row as AdRow;
                              setCreationTabSelections({
                                Campaign: false,
                                "Ad Set": false,
                                Ad: true,
                              });
                              setTabIndex(0);
                              setMetaAdForm({
                                ...metaAdForm,
                                ad_id: adRow.id,
                                meta_id: adRow.meta_id,
                                ad_name: adRow.ad_name,
                                adset_id: adRow.adset_id,
                                adset_meta_id: adRow.adset_meta_id,
                                adset_name: adRow.adset_name,
                                campaign_id: adRow.campaign_id,
                                campaign_meta_id: adRow.campaign_meta_id,
                                campaign_name: adRow.campaign_name,
                                facebook_page_id: adRow.page_id,
                                page_name: adRow.page_name,
                                // instagram_account_id: adRow.instagram_actor_id,
                                creative_format: adRow.format,
                                creative_asset_square:
                                  R.isNil(adRow.image_hash_square) && R.isNil(adRow.video_id_square)
                                    ? undefined
                                    : formatAsset(adRow, SQUARE),
                                creative_asset_horizontal:
                                  R.isNil(adRow.image_hash_horizontal) &&
                                  R.isNil(adRow.video_id_horizontal)
                                    ? undefined
                                    : formatAsset(adRow, HORIZONTAL),
                                creative_asset_vertical:
                                  R.isNil(adRow.image_hash_vertical) &&
                                  R.isNil(adRow.video_id_vertical)
                                    ? undefined
                                    : formatAsset(adRow, VERTICAL),
                                message: adRow.message,
                                headline: adRow.headline,
                                description: adRow.description,
                                call_to_action_type: adRow.call_to_action,
                                url: adRow.link?.split("?")[0],
                                url_params: `?${adRow.link?.split("?")[1]}`,
                                status: adRow.adsmanager_status,
                                client_facing: adRow.client_facing,
                                approval_stage: adRow.approval_stage,
                              });
                              setSelectionsSubmitted(true);
                              goToTab(PageTab.CREATE);
                            } else {
                              openAlertDialog(MBAlertDialogType.EDIT, row);
                            }
                          }}
                          changeLogAction={() => {
                            setSelectedRows(current => {
                              return { ...current, [selectedLevel]: { [row.id]: row } };
                            });
                            setActiveActionMenuRow(undefined);
                            setChangeLogModalActivatedRow(row as AdRow);
                          }}
                        />
                      </div>,
                      document.body
                    )}
                </div>
              </div>
            );
          },
        });
      }

      return headers;
    }, [
      isInternal,
      selectedRows,
      selectedLevel,
      selectRow,
      setSelectedRows,
      setReviewModalActivatedRow,
      reviewModalSetSelectedTab,
      openAlertDialog,
      alertDialogType,
      alertDialogActivatedRow,
      getButtonPosition,
      activeActionMenuRow,
      setSelectionsSubmitted,
      goToTab,
      setCreationTabSelections,
      setTabIndex,
      metaAdForm,
      setMetaAdForm,
      setChangeLogModalActivatedRow,
    ]);

    const tableComponent: JSX.Element = useMetaBuyingTable({
      className: "draftsTable",
      filteredTableData,
      columns: tableColumns,
      headersRenderer: tableHeadersRenderer,
    });

    const [tokens, setTokens] = useState<FilterBarTokens>({
      basic: [],
      advanced: [],
    });
    const filterBar: JSX.Element = useFilterBar({
      pageTab: "drafts",
      tableData,
      isInternal,
      selectedLevel,
      setFilter,
      tokens,
      setTokens,
    });

    return (
      <div className="metaBuyingDrafts">
        {reviewModalComponent}
        {alertDialogComponent}
        {changeLogModalComponent}
        <MetaTableWidget
          isInternal={isInternal}
          title="Drafts"
          selectedAdAccount={selectedAdAccount}
          setSelectedAdAccount={setSelectedAdAccount}
          adAccountOptions={adAccountOptions}
          selectedLevel={selectedLevel}
          setSelectedLevel={setSelectedLevel}
          filterBar={filterBar}
          setFilter={setFilter}
          tableComponent={tableComponent}
          selectedRows={selectedRows}
          openAlertDialog={isInternal ? openAlertDialog : undefined}
          setTokens={setTokens}
        />
      </div>
    );
  }
);

export default MetaBuyingDrafts;
